import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { KpiCard, type KpiHue } from '@/components/fleetguardian';
import { fmtL, fmtNum, fmtPct, fmtR, fmtR2 } from '@/components/fleetguardian/format';
import type { KpiMetric } from '@/types/contract';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });

/** Format a `KpiMetric` value using its `format` hint (single source of truth). */
export function formatMetricValue(metric: KpiMetric): string {
  if (metric.value == null) return '—';
  switch (metric.format) {
    case 'zar':
      return fmtR(metric.value);
    case 'litres':
      return fmtL(metric.value);
    case 'pct':
      return fmtPct(metric.value, 1);
    case 'rand_per_litre':
      return fmtR2(metric.value);
    case 'l_per_100km':
      return `${metric.value.toFixed(1)} L/100km`;
    case 'count':
    default:
      return fmtNum(metric.value);
  }
}

export interface MetricKpiCardProps {
  label: string;
  metric: KpiMetric | undefined;
  icon?: LucideIcon;
  hue?: KpiHue;
  /** When true an upward delta is bad (fraud counts, spend anomalies). */
  invertDelta?: boolean;
  subtitle?: ReactNode;
  className?: string;
}

/**
 * Renders one KPI from the standard `{value, prior, delta_pct, format}`
 * envelope as a fleetGUARDIAN `KpiCard` with a prior-period delta pill.
 * Reusable across Overview + Fraud dashboards (Wave 2C).
 */
export function MetricKpiCard({
  label,
  metric,
  icon,
  hue = 'navy',
  invertDelta,
  subtitle,
  className,
}: MetricKpiCardProps): JSX.Element {
  const value = metric ? formatMetricValue(metric) : '—';
  const delta = metric?.delta_pct ?? null;
  const autoSubtitle =
    metric?.weighted && metric.sample_n != null
      ? `weighted · n=${NUM.format(metric.sample_n)} ${metric.label ?? ''}`.trim()
      : undefined;
  return (
    <KpiCard
      label={label}
      value={value}
      icon={icon}
      hue={hue}
      delta={delta}
      invertDelta={invertDelta}
      subtitle={subtitle ?? autoSubtitle}
      className={className}
    />
  );
}

export default MetricKpiCard;
